import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Loader2 } from "lucide-react";
import { api, FleetCamera, FleetDevice, FleetDrop } from "../api";
import { PageHeader } from "../components/PageHeader";
import { useScope } from "../components/ScopeContext";
import { useRole } from "../hooks/useRole";
import { useWords, type Words } from "../words";

function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function isSimFinished(cam: FleetCamera): boolean {
  if (cam.state === "ended") return true;
  const reason = (cam.health_reason || "").toLowerCase();
  return reason.includes("simulation") && reason.includes("finished");
}

function cameraStatus(cam: FleetCamera): string {
  if (isSimFinished(cam)) return "Finished";
  if (cam.recovering) return "Reconnecting";
  switch (cam.state) {
    case "streaming":
      return "Connected";
    case "pending":
      return "Connecting";
    case "degraded":
      return "Dropped";
    case "dead":
      return "Failed";
    case "ended":
      return "Finished";
    case "rejected":
      return "Resolution limit";
    case "over_capacity":
      return "Over capacity";
    default:
      return cam.state;
  }
}

function statusTone(cam: FleetCamera): string {
  if (isSimFinished(cam)) return "text-bf-muted";
  if (cam.recovering) return "text-amber-300";
  if (cam.healthy) return "text-emerald-300";
  if (cam.state === "degraded") return "text-amber-300";
  return "text-red-300";
}

function nextRetryLabel(cam: FleetCamera): string {
  if (cam.recovering) return "in progress";
  if (cam.next_retry_in_s == null) return "—";
  if (cam.next_retry_in_s <= 1) return "now";
  return `in ${formatDuration(cam.next_retry_in_s)}`;
}

function attemptsLabel(cam: FleetCamera): string {
  const max = cam.recover_max_attempts;
  if (!max || cam.recovery_attempts >= max) return String(cam.recovery_attempts);
  return `${cam.recovery_attempts} / ${max}`;
}

function dropLabel(eventType: string): string {
  switch (eventType) {
    case "source_degraded":
      return "Dropped";
    case "source_recovery_started":
      return "Retry started";
    case "source_recovery_failed":
      return "Retry failed";
    case "source_recovery_exhausted":
      return "Retries exhausted";
    case "source_held":
      return "Stopped";
    case "source_error":
      return "Error";
    case "source_dead":
      return "Failed";
    default:
      return eventType.replace(/_/g, " ");
  }
}

function deviceTone(status: FleetDevice["status"]): string {
  if (status === "down") return "text-red-300";
  if (status === "drop") return "text-amber-300";
  return "text-emerald-300";
}

function deviceDetail(device: FleetDevice, w: Words, isStaff: boolean): string {
  if (device.kind === "connector") {
    const seen = device.last_seen_at
      ? `last seen ${new Date(device.last_seen_at).toLocaleString()}`
      : "never connected";
    return [w.Box, device.host, seen].filter(Boolean).join(" · ");
  }
  const kind = device.kind === "nvr" ? w.Recorder : isStaff ? "Host" : "Camera";
  const address = isStaff && device.host ? ` · ${device.host}:${device.port}` : "";
  const via = device.via ? ` via ${device.via}` : "";
  return `${kind}${address}${via}`;
}

function deviceStatusLabel(status: FleetDevice["status"]): string {
  if (status === "down") return "Down";
  if (status === "drop") return "Drop";
  return "Connected";
}

export default function Dashboard() {
  const { scopeParams, scopeKey } = useScope();
  const w = useWords();
  const { isStaff } = useRole();
  const health = useQuery({
    queryKey: ["fleet-health", scopeKey],
    queryFn: () => api.dashboard.health(scopeParams),
    refetchInterval: 3000,
  });

  const data = health.data;
  const cameras = data?.cameras ?? [];
  const attention = cameras.filter((c) => c.connection_issue);
  const devicesInTrouble = (data?.devices ?? []).filter((d) => d.status !== "ok");
  const connected = cameras.filter((c) => c.healthy && !c.recovering).length;
  const reconnecting = cameras.filter(
    (c) => c.recovering || (c.connection_issue && c.state !== "dead"),
  ).length;
  const failed = cameras.filter((c) => c.connection_issue && c.state === "dead" && !c.recovering).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        icon={LayoutDashboard}
        description={`Connection health for cameras, ${w.recorders} and ${w.boxes}.`}
      />

      {health.isLoading && (
        <div className="bf-card flex items-center gap-3 p-6 text-bf-muted">
          <Loader2 size={20} className="animate-spin" />
          Loading connection health…
        </div>
      )}

      {health.isError && (
        <div className="bf-card p-4 text-sm text-red-300">
          Failed to load connection health.
        </div>
      )}

      {data && (
        <>
          {data.pipeline !== "ready" && (
            <div className="bf-card p-3 text-sm text-amber-200">
              {isStaff
                ? `Ingest is ${data.pipeline}. Camera rows still come from the configured fleet.`
                : "Live video processing is not available right now, so camera status may be out of date."}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard label="Connected" value={connected} />
            <SummaryCard label="Reconnecting" value={reconnecting} />
            <SummaryCard label="Failed" value={failed} />
            <SummaryCard label="Devices with a drop" value={devicesInTrouble.length} />
          </div>

          <section>
            <h2 className="mb-2 text-xs uppercase tracking-wide text-bf-muted">
              Devices
            </h2>
            {devicesInTrouble.length === 0 ? (
              <div className="bf-card p-4 text-sm text-bf-muted">
                {isStaff
                  ? "No connector, recorder or host has a dropped connection."
                  : `No ${w.box}, ${w.recorder} or camera has a dropped connection.`}
              </div>
            ) : (
              <div className="bf-card overflow-x-auto p-0">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
                    <tr>
                      <th className="p-3">Device</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Cameras</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devicesInTrouble.map((device) => (
                      <tr key={device.id} className="border-t border-white/5">
                        <td className="p-3">
                          <div className="font-medium text-white">{device.name}</div>
                          <div className="text-xs text-bf-muted">
                            {deviceDetail(device, w, isStaff)}
                          </div>
                        </td>
                        <td className={`p-3 text-xs ${deviceTone(device.status)}`}>
                          {deviceStatusLabel(device.status)}
                        </td>
                        <td className="p-3 text-xs tabular-nums text-bf-muted">
                          {device.unhealthy_count} of {device.camera_count} down
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-2 text-xs uppercase tracking-wide text-bf-muted">
              Cameras that need attention
            </h2>
            {attention.length === 0 ? (
              <div className="bf-card p-4 text-sm text-bf-muted">
                No connection problems in this view.
              </div>
            ) : (
              <CameraTable cameras={attention} />
            )}
          </section>

          <section>
            <h2 className="mb-2 text-xs uppercase tracking-wide text-bf-muted">
              Recent connection events
            </h2>
            <DropsList drops={data.recent_drops} />
          </section>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bf-card p-4">
      <div className="text-xs uppercase tracking-wide text-bf-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-white">{value}</div>
    </div>
  );
}

function CameraTable({ cameras }: { cameras: FleetCamera[] }) {
  return (
    <div className="bf-card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
          <tr>
            <th className="p-3">Camera</th>
            <th className="p-3">Status</th>
            <th className="p-3">Retries</th>
            <th className="p-3">Next retry</th>
            <th className="p-3">Detail</th>
          </tr>
        </thead>
        <tbody>
          {cameras.map((cam) => (
            <tr key={cam.id} className="border-t border-white/5">
              <td className="p-3">
                <div className="font-medium text-white">{cam.name || cam.id}</div>
                <div className="text-xs text-bf-muted">
                  {[cam.store, cam.device_name].filter(Boolean).join(" · ")}
                </div>
              </td>
              <td className={`p-3 text-xs ${statusTone(cam)}`}>{cameraStatus(cam)}</td>
              <td className="p-3 text-xs tabular-nums text-bf-muted">{attemptsLabel(cam)}</td>
              <td className="p-3 text-xs tabular-nums text-bf-muted">{nextRetryLabel(cam)}</td>
              <td className="max-w-xs p-3 text-xs text-bf-muted">
                {cam.health_reason || cam.last_error || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DropsList({ drops }: { drops: FleetDrop[] }) {
  if (drops.length === 0) {
    return (
      <div className="bf-card p-4 text-sm text-bf-muted">
        No connection drops in the last 24 hours.
      </div>
    );
  }
  return (
    <div className="bf-card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
          <tr>
            <th className="p-3">When</th>
            <th className="p-3">Event</th>
            <th className="p-3">Camera</th>
            <th className="p-3">Device</th>
            <th className="p-3">Detail</th>
          </tr>
        </thead>
        <tbody>
          {drops.map((drop, i) => (
            <tr key={`${drop.ts}-${drop.camera_id}-${i}`} className="border-t border-white/5">
              <td className="whitespace-nowrap p-3 text-xs tabular-nums text-bf-muted">
                {new Date(drop.ts).toLocaleString()}
              </td>
              <td className="p-3 text-xs text-white/90">{dropLabel(drop.event_type)}</td>
              <td className="p-3 text-xs text-bf-muted">{drop.camera_name || drop.camera_id || "—"}</td>
              <td className="p-3 text-xs text-bf-muted">{drop.device_name || "—"}</td>
              <td className="max-w-sm p-3 text-xs text-bf-muted">{drop.reason || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
